import {
    FlowBlock, FlowProperty,
    FlowValidationError
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {ValidationMessages} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowValidationController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {FlowPropertiesUtils} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/FlowPropertiesUtils';
import {IWidgetComponentProps} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/FlowPropertiesWidgets';
import {Col} from '@mintdata/mint-kit/impl/properties/components/col/Col';
import {EditableList} from '@mintdata/mint-kit/impl/properties/components/editableList/EditableList';
import {Row} from '@mintdata/mint-kit/impl/properties/components/row/Row';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import bind from 'bind-decorator';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import _get from 'lodash/get';

interface IMapWidgetItem
{
    key:any;
    value:any;
}

interface IMapWidgetMeta
{
    items:IMapWidgetItem[] | null;
}

interface IMapWidgetdState
{
    newKey:any;
    newValue:any;
    isNewKeyDirty:boolean;
    isNewValueDirty:boolean;
}

@observer
export class MapWidget extends React.Component<IWidgetComponentProps, any>
{
    @observable
    private _state:IMapWidgetdState;

    private _focusElement:HTMLInputElement;
    private _refHandlers = {
        focus: (el:HTMLInputElement) => this._focusElement = el,
    };

    constructor(props:IWidgetComponentProps)
    {
        super(props);

        const {prop} = props;
        const {definition} = prop;

        const {key: keyDefinition, value: valueDefinition} = definition.mapInfo;

        this._state = {
            newKey: keyDefinition.defaultValue,
            newValue: valueDefinition.defaultValue,
            isNewKeyDirty: false,
            isNewValueDirty: false,
        };
    }

    public componentDidMount()
    {
        const {prop, onMetaChange} = this.props;
        const {value, meta} = prop;

        const items = _get(meta, 'items');

        if( value && Object.keys(value).length > 0 && !items )
        {
            const newMeta:IMapWidgetMeta = {items: this._getItemsFromValue(value)};

            onMetaChange(newMeta);
        }
    }

    public render()
    {
        const {prop, block, items} = this._getProps();
        const {definition} = prop;
        const {mapInfo} = definition;
        const {key: keyDefinition, value: valueDefinition} = mapInfo;

        const {newKey, newValue} = this._state;

        const newKeyProp = new FlowProperty(keyDefinition);
        const newValueProp = new FlowProperty(valueDefinition);

        newKeyProp.value = newKey;
        newValueProp.value = newValue;

        const newKeyError = this._validateNewKeyProp(newKeyProp, prop, block);
        const newValueError = this._validateNewValueProp(newValueProp, prop, block);

        const isNewKeyValid = !newKeyError;
        const isNewValueValid = !newValueError;

        return (
            <div>
                <EditableList
                    isValid={isNewKeyValid && isNewValueValid}
                    times={items.length}
                    focusElement={this._focusElement}
                    itemRenderer={this._itemRenderer}
                    formRenderer={this._formRenderer(newKeyProp, newKeyError, newValueProp, newValueError)}
                    onAdd={this._onItemAdd}
                    onRemove={this._onItemRemove}
                    onMove={this._onItemMove}
                />
            </div>
        );
    }

    private _formRenderer = (newKeyProp:FlowProperty,
                             newKeyError:FlowValidationError,
                             newValueProp:FlowProperty,
                             newValueError:FlowValidationError) =>
    {
        return () =>
        {
            const {prop, block, frameColumnNames} = this._getProps();
            const {key: keyDefinition, value: valueDefinition} = prop.definition.mapInfo;

            const {isNewKeyDirty, isNewValueDirty} = this._state;

            const KeyWidget = FlowPropertiesUtils.getWidgetComponent(keyDefinition);
            const ValueWidget = FlowPropertiesUtils.getWidgetComponent(valueDefinition);

            return (
                <Row>
                    <Col size={2}>
                        <KeyWidget
                            prop={newKeyProp}
                            block={block}
                            error={newKeyError}
                            frameColumnNames={frameColumnNames}
                            inlineLabel={FlowPropertiesUtils.getPropInlineLabel(newKeyProp)}
                            isDirty={isNewKeyDirty}
                            onChange={this._onNewKeyChange}
                            onRef={this._refHandlers.focus}
                        />
                    </Col>
                    <Col size={2}>
                        <ValueWidget
                            prop={newValueProp}
                            block={block}
                            error={newValueError}
                            frameColumnNames={frameColumnNames}
                            inlineLabel={FlowPropertiesUtils.getPropInlineLabel(newValueProp)}
                            isDirty={isNewValueDirty}
                            onChange={this._onNewValueChange}
                        />
                    </Col>
                </Row>
            );
        };
    };

    private _itemRenderer = (index:number) =>
    {
        const fppController = controllers.ui.flowPropertiesPanel;

        const {prop, block, items, frameColumnNames, error} = this._getProps();
        const {key: keyDefinition, value: valueDefinition} = prop.definition.mapInfo;

        const item = items[index];
        const keyProp = new FlowProperty(keyDefinition);
        const valueProp = new FlowProperty(valueDefinition);

        keyProp.value = item.key;
        valueProp.value = item.value;

        const nestedErrors = _get(error, 'nested', []) as FlowValidationError[];

        const keyError = nestedErrors.find(({flowPropertyName}) => (
            flowPropertyName === fppController.getArrayPropertyName(keyDefinition.name, index)
        ));

        const valueError = nestedErrors.find(({flowPropertyName}) => (
            flowPropertyName === fppController.getArrayPropertyName(valueDefinition.name, index)
        ));

        const KeyWidget = FlowPropertiesUtils.getWidgetComponent(keyDefinition);
        const ValueWidget = FlowPropertiesUtils.getWidgetComponent(valueDefinition);

        // todo: implement isDirty logic for nested properties
        return (
            <Row>
                <Col size={2}>
                    <KeyWidget
                        prop={keyProp}
                        block={block}
                        error={keyError}
                        frameColumnNames={frameColumnNames}
                        onChange={this._onKeyChange(index)}
                        isOutlined={false}
                        isDirty={true}
                    />
                </Col>
                <Col size={2}>
                    <ValueWidget
                        prop={valueProp}
                        block={block}
                        error={valueError}
                        frameColumnNames={frameColumnNames}
                        onChange={this._onValueChange(index)}
                        isOutlined={false}
                        isDirty={true}
                    />
                </Col>
            </Row>
        );
    };

    private _getProps()
    {
        const {prop} = this.props;
        const {value, meta} = prop;

        const items:IMapWidgetItem[] = meta ? meta.items : [];

        return {
            value: value || {},
            items,
            ...this.props,
        };
    }

    private _getItemsFromValue(value:object):IMapWidgetItem[]
    {
        return Object.keys(value).map((key) => ({key, value: value[key]}));
    }

    private _getValueFromItems(items:IMapWidgetItem[]):object
    {
        return items.reduce((result, {key, value}) =>
        {
            result[key] = value;
            return result;
        }, {});
    }

    private _validateNewKeyProp(prop:FlowProperty, mapProp:FlowProperty, block:FlowBlock):FlowValidationError
    {
        const fvController = controllers.ui.flowValidation;

        let newKeyError = fvController.validateFlowProperties([prop], block)[0];
        const isUnique = fvController.isMapWidgetNewKeyUnique(prop.value, mapProp);

        if( !isUnique )
        {
            if( newKeyError )
            {
                newKeyError.messages.push(ValidationMessages.uniqueKey);
            }
            else
            {
                newKeyError = FlowValidationError.propertyError(
                    [ValidationMessages.uniqueKey],
                    block.id,
                    block.name,
                    prop.definition.name,
                );
            }
        }

        return newKeyError;
    }

    private _validateNewValueProp(prop:FlowProperty, mapProp:FlowProperty, block:FlowBlock):FlowValidationError
    {
        const fvController = controllers.ui.flowValidation;
        return fvController.validateFlowProperties([prop], block)[0];
    }

    @action.bound
    private _onNewKeyChange(value:any)
    {
        this._state.newKey = value;
        this._state.isNewKeyDirty = true;
    }

    @action.bound
    private _onNewValueChange(value:any)
    {
        this._state.newValue = value;
        this._state.isNewValueDirty = true;
    }

    @bind
    private _onKeyChange(index:number)
    {
        const {items, onChange, onMetaChange} = this._getProps();
        const newItems = [...items];

        return action((key:any) =>
        {
            newItems[index].key = key;

            const newPropValue = this._getValueFromItems(newItems);

            onChange(newPropValue);
            onMetaChange({items: newItems});
        });
    }

    @bind
    private _onValueChange(index:number)
    {
        const {items, onChange, onMetaChange} = this._getProps();
        const newItems = [...items];

        return action((value:any) =>
        {
            newItems[index].value = value;

            const newPropValue = this._getValueFromItems(newItems);

            onChange(newPropValue);
            onMetaChange({items: newItems});
        });
    }

    @action.bound
    private _onItemAdd()
    {
        const {prop, items, onChange, onMetaChange} = this._getProps();
        const {definition} = prop;
        const {key: keyDefinition, value: valueDefinition} = definition.mapInfo;

        const {newKey, newValue} = this._state;
        const newItems = [...items, {key: newKey, value: newValue}];
        const newPropValue = this._getValueFromItems(newItems);

        onChange(newPropValue);
        onMetaChange({items: newItems});

        this._state = {
            newKey: keyDefinition.defaultValue,
            newValue: valueDefinition.defaultValue,
            isNewKeyDirty: false,
            isNewValueDirty: false,
        };
    }

    @action.bound
    private _onItemRemove(index:number)
    {
        const {items, onChange, onMetaChange} = this._getProps();
        const newItems = [...items];

        newItems.splice(index, 1);

        const newPropValue = this._getValueFromItems(newItems);

        onChange(newPropValue);
        onMetaChange({items: newItems});
    }

    @bind
    private _onItemMove(from:number, to:number)
    {
        const {items, onMetaChange} = this._getProps();
        const newItems = ArrayUtils.reorderArray(items, from, to);

        onMetaChange({items: newItems});
    }
}
